import React, { useEffect, useState, useCallback } from "react";
import ApiService from "../../../service/ApiService";
import { apiPaths } from "../../../service/apiPaths";
import { X, Filter, Star, ArrowDown } from "lucide-react";

const ProductFilter = ({
  filters,
  loading,
  hasFilterChanges,
  handleFilterChange,
  applyFilters,
  resetFilters,
  selectedCategory,
  setSelectedCategory,
  productType, // Add productType prop
}) => {
  const [categories, setCategories] = useState(null);
  const [filterOptions, setFilterOptions] = useState({
    brands: [],
    colors: [],
    sizes: [],
    priceRange: { min: 0, max: 100000 }
  });
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  
  // Local filter state - these DON'T trigger API calls until Apply is clicked
  const [localFilters, setLocalFilters] = useState({
    sortBy: filters.sortBy || "",
    priceFrom: filters.priceFrom || "",
    priceTo: filters.priceTo || "",
    rating: filters.rating || "",
    inStock: filters.inStock || false,
    onSale: filters.onSale || false,
    brand: filters.brand || "",
  });

  // Update local state when parent filters change (like on reset)
  useEffect(() => {
    setLocalFilters({
      sortBy: filters.sortBy || "",
      priceFrom: filters.priceFrom || "",
      priceTo: filters.priceTo || "",
      rating: filters.rating || "",
      inStock: filters.inStock || false,
      onSale: filters.onSale || false,
      brand: filters.brand || "",
    });
  }, [filters]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch categories
        const categoriesResponse = await ApiService({
          method: "GET",
          endpoint: apiPaths.getCategories,
        });
        if (categoriesResponse?.data) {
          setCategories(categoriesResponse.data);
        }

        // Fetch filter options based on productType
        const filterParams = productType ? `?productType=${productType}` : '';
        const filtersResponse = await ApiService({
          method: "GET",
          endpoint: `${apiPaths.getProductFilters}${filterParams}`,
        });
        if (filtersResponse?.success && filtersResponse?.data) {
          setFilterOptions({
            brands: filtersResponse.data.brands || [],
            colors: filtersResponse.data.colors || [],
            sizes: filtersResponse.data.sizes || [],
            priceRange: filtersResponse.data.priceRange || { min: 0, max: 100000 }
          });
        }
      } catch (error) {
        console.error("Error fetching filter data:", error);
        setCategories(null);
      }
    };
    fetchData();
  }, [productType]);

  const closeFilter = useCallback(() => {
    setIsFilterOpen(false);
  }, []);

  // Handle local filter changes - NO API CALLS
  const handleLocalFilterChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setLocalFilters(prev => ({
      ...prev,
      [name]: newValue
    }));
  }, []);

  // Handle category change - this can trigger immediate API call
  const handleCategoryChange = useCallback((e) => {
    const selected = categories?.find(
      (cat) => cat._id === e.target.value
    );
    setSelectedCategory(selected || { _id: "", name: "All" });
  }, [categories, setSelectedCategory]);

  // Check if local filters are different from applied filters
  const hasLocalChanges = useCallback(() => {
    return (
      localFilters.sortBy !== filters.sortBy ||
      localFilters.priceFrom !== filters.priceFrom ||
      localFilters.priceTo !== filters.priceTo ||
      localFilters.rating !== filters.rating ||
      localFilters.inStock !== filters.inStock ||
      localFilters.onSale !== filters.onSale ||
      localFilters.brand !== filters.brand
    );
  }, [localFilters, filters]);

  // Handle reset
  const handleReset = useCallback(() => {
    const resetValues = {
      sortBy: "",
      priceFrom: "",
      priceTo: "",
      rating: "",
      inStock: false,
      onSale: false,
      brand: "",
    };
    
    setLocalFilters(resetValues);
    resetFilters();
    closeFilter();
  }, [resetFilters, closeFilter]);

  // Handle apply filters - ONLY NOW we trigger API calls
  const handleApply = useCallback(() => {
    // Apply all local filters to parent state
    Object.keys(localFilters).forEach(key => {
      handleFilterChange({
        target: { name: key, value: localFilters[key] }
      });
    });
    
    // Small delay to ensure state is updated, then trigger API call
    setTimeout(() => {
      applyFilters();
      closeFilter();
    }, 100);
  }, [localFilters, handleFilterChange, applyFilters, closeFilter]);

  return (
    <div className="relative">
      {/* Toggle Button */}
      <button
        onClick={() => setIsFilterOpen(true)}
        className="px-4 py-2 bg-[#182134] text-white rounded-md transition-all duration-300 focus:outline-none focus:ring-offset-2 shadow-md hover:bg-[#2a3441]"
      >
        <div className="flex items-center space-x-2">
          <Filter size={18} />
          <span className="font-medium">Filters</span>
          {hasLocalChanges() && (
            <span className="ml-1 w-2 h-2 bg-red-500 rounded-full"></span>
          )}
        </div>
      </button>

      {/* Filter Box & Background Overlay */}
      {isFilterOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-end transition-opacity duration-300 ease-in-out">
          {/* Clickable Background to Close */}
          <div className="absolute inset-0" onClick={closeFilter}></div>

          {/* Filter Box */}
          <div className="relative w-[350px] md:w-[400px] bg-white shadow-2xl h-full overflow-hidden animate-slide-in">
            {/* Header */}
            <div className="pt-6 px-6">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-800">Filters</h2>
                <button
                  onClick={closeFilter}
                  className="text-black hover:text-[#616161] hover:rotate-180 transition-all"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            {/* Filter Content */}
            <div className="px-6 overflow-auto h-[calc(100%-160px)]">
              <div className="flex flex-col gap-6">
                {/* Category Dropdown */}
                <div className="space-y-3">
                  <label className="block text-sm font-semibold text-gray-800">
                    Category
                  </label>
                  <div className="relative">
                    <select
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white shadow-sm appearance-none text-gray-700"
                      value={selectedCategory?._id || ""}
                      onChange={handleCategoryChange}
                    >
                      <option value="">All Categories</option>
                      {Array.isArray(categories) &&
                        categories.map((category) => (
                          <option key={category._id} value={category._id}>
                            {category.categoryName || category.name}
                          </option>
                        ))}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-700">
                      <ArrowDown size={16} />
                    </div>
                  </div>
                </div>

                {/* Sort By */}
                <div className="space-y-3">
                  <label className="block text-sm font-semibold text-gray-800">
                    Sort By
                  </label>
                  <div className="relative">
                    <select
                      name="sortBy"
                      value={localFilters.sortBy}
                      onChange={handleLocalFilterChange}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white shadow-sm appearance-none text-gray-700"
                      disabled={loading}
                    >
                      <option value="">Relevance</option>
                      <option value="na">Name A-Z</option>
                      <option value="nz">Name Z-A</option>
                      <option value="d">Best Discount</option>
                      <option value="pl">Price: Low to High</option>
                      <option value="ph">Price: High to Low</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-700">
                      <ArrowDown size={16} />
                    </div>
                  </div>
                </div>

                {/* Price Range - Now uses local state, no API calls on input */}
                <div className="space-y-3">
                  <label className="block text-sm font-semibold text-gray-800">
                    Price Range
                  </label>
                  <div className="flex space-x-4">
                    <div className="flex-1 relative">
                      <input
                        type="number"
                        name="priceFrom"
                        placeholder="Min Price"
                        value={localFilters.priceFrom}
                        onChange={handleLocalFilterChange}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white shadow-sm text-gray-700"
                        disabled={loading}
                      />
                    </div>
                    <div className="flex-1 relative">
                      <input
                        type="number"
                        name="priceTo"
                        placeholder="Max Price"
                        value={localFilters.priceTo}
                        onChange={handleLocalFilterChange}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white shadow-sm text-gray-700"
                        disabled={loading}
                      />
                    </div>
                  </div>
                </div>

                {/* Customer Ratings */}
                <div className="space-y-3">
                  <label className="block text-sm font-semibold text-gray-800">
                    Customer Ratings
                  </label>
                  <div className="relative">
                    <select
                      name="rating"
                      value={localFilters.rating}
                      onChange={handleLocalFilterChange}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white shadow-sm appearance-none text-gray-700"
                      disabled={loading}
                    >
                      <option value="">All Ratings</option>
                      <option value="5">5★ Only</option>
                      <option value="4">4★ & above</option>
                      <option value="3">3★ & above</option>
                      <option value="2">2★ & above</option>
                      <option value="1">1★ & above</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-700">
                      <ArrowDown size={16} />
                    </div>
                  </div>
                </div>

                {/* Availability */}
                <div className="space-y-3">
                  <label className="block text-sm font-semibold text-gray-800">
                    Availability
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="inStock"
                        checked={localFilters.inStock}
                        onChange={handleLocalFilterChange}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                        disabled={loading}
                      />
                      <span className="text-sm text-gray-700">In Stock Only</span>
                    </label>
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="onSale"
                        checked={localFilters.onSale}
                        onChange={handleLocalFilterChange}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                        disabled={loading}
                      />
                      <span className="text-sm text-gray-700">On Sale</span>
                    </label>
                  </div>
                </div>

                {/* Brand Filter */}
                <div className="space-y-3">
                  <label className="block text-sm font-semibold text-gray-800">
                    Brand
                  </label>
                  <div className="relative">
                    <select
                      name="brand"
                      value={localFilters.brand}
                      onChange={handleLocalFilterChange}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white shadow-sm appearance-none text-gray-700"
                      disabled={loading}
                    >
                      <option value="">All Brands</option>
                      {filterOptions.brands.map((brand) => (
                        <option key={brand} value={brand}>
                          {brand}
                        </option>
                      ))}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-700">
                      <ArrowDown size={16} />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer with Apply and Reset Buttons */}
            <div className="absolute bottom-0 left-0 right-0 p-6 bg-gray-50 border-t border-gray-200">
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleReset}
                  disabled={loading}
                  className="flex-1 px-4 py-3 bg-white text-gray-700 rounded-lg hover:bg-gray-100 focus:ring-2 focus:ring-gray-500 font-medium border border-gray-300 transition-colors duration-300 disabled:opacity-50"
                >
                  Reset All
                </button>
                <button
                  onClick={handleApply}
                  disabled={loading}
                  className={`flex-1 px-4 py-3 rounded-lg font-medium shadow-md transition-all duration-300 disabled:opacity-50 ${
                    hasLocalChanges() 
                      ? 'bg-[#182134] hover:bg-[#2a3441] text-white' 
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {loading ? 'Applying...' : `Apply Filters${hasLocalChanges() ? ` (${Object.values(localFilters).filter(v => v && v !== "").length})` : ''}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Add CSS animation
const style = document.createElement('style');
style.textContent = `
  @keyframes slide-in {
    from {
      transform: translateX(100%);
    }
    to {
      transform: translateX(0);
    }
  }
  .animate-slide-in {
    animation: slide-in 0.3s ease-out forwards;
  }
`;
if (!document.querySelector('#filter-animations')) {
  style.id = 'filter-animations';
  document.head.appendChild(style);
}

export default React.memo(ProductFilter);